import {
  MenubarCheckboxItem,
  MenubarSub,
  MenubarSubContent,
  MenubarSubTrigger,
} from "@/components/ui/menubar";
import { useFormFieldVisibility } from "@/context/FormFieldVisibilityContext";
import { groupFieldsByBaseLocale } from "@/lib/form-field-visibility";
import { tWithKey } from "@/lib/i18n-fallback";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

export type VisibilityMenuTab = {
  value: string;
  /** Display-ready label (already translated). */
  label: string;
  /** When true, the tab cannot be hidden. */
  nonHideable?: boolean;
};

// Grey hover matching the project's customized MenubarItem (accent is blue here).
const GREY_HOVER =
  "hover:bg-slate-200 focus:bg-slate-200 focus:text-accent-foreground data-[highlighted]:bg-slate-200 data-[state=open]:bg-slate-200";

/**
 * "Onglets" + "Champs" submenus to drop inside a bespoke sheet-form Actions
 * menu (Menubar). Mirrors the drawer/dropdown used by AbstractForm, but for
 * sub-table forms (Exemplaire, Tirage, Sculpture) that render their own tabs.
 *
 * `activeTab` scopes the "Champs" submenu to the currently mounted tab, whose
 * fields have self-registered in the visibility grid.
 */
export function FormVisibilityMenuItems({
  tabs,
  activeTab,
}: {
  tabs: readonly VisibilityMenuTab[];
  activeTab?: string;
}) {
  const { t } = useTranslation();
  const {
    enabled,
    isTabVisible,
    toggleTabVisibility,
    getRegisteredFields,
    registryVersion,
    isExpanded,
    toggleVisibility,
  } = useFormFieldVisibility();

  // One entry per field, multilingual variants (x.fr, x.en…) merged into one.
  const fieldGroups = useMemo(
    () =>
      groupFieldsByBaseLocale(
        getRegisteredFields().filter(
          // Sheet-level fields (no tab) show everywhere; else match the tab.
          (f) => f.tab == null || activeTab == null || f.tab === activeTab,
        ),
      ),
    // registryVersion changes whenever a field mounts/unmounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getRegisteredFields, registryVersion, activeTab],
  );

  if (!enabled) return null;

  const isGroupShown = (names: string[]) => names.every(isExpanded);
  const setGroupShown = (names: string[], shown: boolean) =>
    names.forEach((n) => {
      if (isExpanded(n) !== shown) toggleVisibility(n);
    });

  const hideableTabs = tabs.filter((tab) => !tab.nonHideable);

  return (
    <>
      {hideableTabs.length > 0 && (
        <MenubarSub>
          <MenubarSubTrigger className={`gap-2 ${GREY_HOVER}`}>
            {tWithKey(t, "fieldVisibility.tabs.manage", "Onglets")}
          </MenubarSubTrigger>
          <MenubarSubContent className="max-h-[60vh] overflow-y-auto">
            {hideableTabs.map((tab) => (
              <MenubarCheckboxItem
                key={tab.value}
                checked={isTabVisible(tab.value)}
                className={GREY_HOVER}
                onSelect={(e) => e.preventDefault()}
                onCheckedChange={() => toggleTabVisibility(tab.value)}
              >
                {tab.label}
              </MenubarCheckboxItem>
            ))}
          </MenubarSubContent>
        </MenubarSub>
      )}

      {fieldGroups.length > 0 && (
        <MenubarSub>
          <MenubarSubTrigger className={`gap-2 ${GREY_HOVER}`}>
            {tWithKey(t, "fieldVisibility.tab.fields", "Champs")}
          </MenubarSubTrigger>
          <MenubarSubContent className="max-h-[60vh] overflow-y-auto">
            {fieldGroups.map((group) => {
              const shown = isGroupShown(group.names);
              const displayLabel = group.label ?? t(group.baseKey);
              return (
                <MenubarCheckboxItem
                  key={group.baseKey}
                  checked={group.nonMaskable || shown}
                  disabled={group.nonMaskable}
                  className={GREY_HOVER}
                  onSelect={(e) => e.preventDefault()}
                  onCheckedChange={() =>
                    !group.nonMaskable && setGroupShown(group.names, !shown)
                  }
                >
                  {displayLabel}
                </MenubarCheckboxItem>
              );
            })}
          </MenubarSubContent>
        </MenubarSub>
      )}
    </>
  );
}
